resourcePaths = '{"rootPath":"/member","assetsPath":"/member/assets"}';/*
    Session

    A Singleton implementation to manage a user's session.

    Provides the ability to create and delete sessions. Manages session expiration
    and authorization tokens across pages, including pages that are not in the
    authorized realm. Includes optimized "keep-alive" functionality to keep a
    session alive.

    See below for example usage. This object should be initialized on every page
    in which Session management should be enabled in that domain.

    Methods (see methods for additional usage):

        createSession(data, callback)

        deleteSession(callback)

        getAuthToken

        setAuthToken(token)

        deleteAuthToken

        addWhitelistUri(uri)

        removeWhitelistUri(uri)

    Example Usage:

        var s = new Session();
        s.createSession({username: "User", password: "password"}, function() {
            alert("logged in!");
        });
        $("#logout-link").on("click", s.deleteSession(function(){
            alert("logged out!");
        }));

        For pages that do not require creating or deleting sessions, simply
        initialize the Session object:

            new Session();
*/
function Session () {
  this._auth_token = '';
  this._bearer_token = '';
  this._refresh_token = '';
  this._sessionExpirationCallback = function () {};
  this._sessionTimeoutWarningCallback = function () {};
  this._timer = null;
  this._timeoutWarning = null;
  this._pendingPing = null;
  this._binded = false;
  this._cookieSalt = 'session__';
  this._whitelistUris = [];

  this._SESSION_TIMEOUT = 900000;
  this._SESSION_TIMEOUT_WARNING = 720000;
  this._SESSION_TIMEOUT_COUNTDOWN = 0;
  this._MIN_TIME_BTWN_PINGS = 10000;

  this._getCookieDomain = function () {
    var hostname = window.location.hostname || '';

    // Handle *.mogo.ca domains (production and lower environments)
    var mogoCaSuffix = '.mogo.ca';
    if (hostname.slice(-mogoCaSuffix.length) === mogoCaSuffix) {
      var mogoPrefix = hostname.slice(0, hostname.length - mogoCaSuffix.length);
      if (mogoPrefix) {
        var mogoSegments = mogoPrefix.split('.');
        var lastSegment = mogoSegments[mogoSegments.length - 1];
        var environments = ['qa', 'dev', 'staging', 'prod'];
        if (environments.indexOf(lastSegment) !== -1) {
          return '.' + lastSegment + mogoCaSuffix;
        }
      }
      return mogoCaSuffix;
    }

    // Handle legacy *.mogocloud.ca and *.mogofintech.ca domains
    var scopedSuffixes = ['.mogocloud.ca', '.mogofintech.ca'];
    for (var i = 0; i < scopedSuffixes.length; i++) {
      var suffix = scopedSuffixes[i];
      if (hostname.slice(-suffix.length) === suffix) {
        var prefix = hostname.slice(0, hostname.length - suffix.length);
        if (!prefix) {
          return suffix;
        }

        var segments = prefix.split('.');
        var env = segments.pop();

        if (env) {
          return '.' + env + suffix;
        }

        return suffix;
      }
    }

    return null;
  };

  this.constructor = function () {
    // Set cookies
    if (Cookies.get('X-Auth-Token') != null) {
      this.setAuthToken(Cookies.get('X-Auth-Token'));
    } else {
      this._auth_token = '';
    }

    if (Cookies.get('X-Bearer-Token') != null) {
      this.setBearerToken(Cookies.get('X-Bearer-Token'));
    } else {
      this._bearer_token = '';
    }

    if (Cookies.get('X-Refresh-Token') != null) {
      this.setRefreshToken(Cookies.get('X-Refresh-Token'));
    } else {
      this._refresh_token = '';
    }

    this._SESSION_TIMEOUT_COUNTDOWN =
      (this._SESSION_TIMEOUT - this._SESSION_TIMEOUT_WARNING) / 1000;
  };

  /*
        Get the authorization token.

        Returns:
            The authorization token, if it exists. Otherwise, returns
            an empty string.
    */
  this.getAuthToken = function () {
    return this._auth_token;
  };

  this.getBearerToken = function () {
    return this._bearer_token;
  };

  this.getRefreshToken = function () {
    return this._refresh_token;
  };

  /*
        Set the authorization token. Initializes the session on the front-end.
        If the token is invalid, the user will be unable to access any authorized
        resources.

        Arguments:
            token [string] (required):
                The authorization token to use for session management.
    */
  this.setAuthToken = function (token) {
    this._auth_token = token;
    var cookieOptions = { path: '/', secure: true };
    var cookieDomain = this._getCookieDomain();
    if (cookieDomain) {
      cookieOptions.domain = cookieDomain;
    }
    Cookies.set('X-Auth-Token', token, cookieOptions);
    this._queuePing(3000);

    // Bind callback function after each ajax request
    if (!this._binded) {
      $(document).ajaxSend($.proxy(this._onAjaxRequest, this));
      this._binded = true;
    }
  };

  this.setBearerToken = function (token) {
    this._bearer_token = token;
    var cookieOptions = {path: '/', secure: true};
    var cookieDomain = this._getCookieDomain();
    if (cookieDomain) {
      cookieOptions.domain = cookieDomain;
    }
    Cookies.set('X-Bearer-Token', token, cookieOptions);
  };

  this.setRefreshToken = function (token) {
    this._refresh_token = token;
    var cookieOptions = {path: '/', secure: true};
    var cookieDomain = this._getCookieDomain();
    if (cookieDomain) {
      cookieOptions.domain = cookieDomain;
    }
    Cookies.set('X-Refresh-Token', token, cookieOptions);
  };

  /*
        Delete the authorization token. Removes the session from the front-end but
        the user still has a session in the back-end.
    */
  this.deleteAuthToken = function () {
    var cookieDomain = this._getCookieDomain();
    if (cookieDomain) {
      Cookies.remove('X-Auth-Token', { path: '/', domain: cookieDomain });
    }
    Cookies.remove('X-Auth-Token', { path: '/' });
    this._auth_token = '';
    this._clearTimer();

    // Unbind callback function after each ajax request
    if (this._binded) {
      $(document).unbind('ajaxSend');
      this._binded = false;
    }
  };

  this.deleteBearerToken = function () {
    var cookieDomain = this._getCookieDomain();
    if (cookieDomain) {
      Cookies.remove('X-Bearer-Token', {path: '/', domain: cookieDomain});
    }
    Cookies.remove('X-Bearer-Token', {path: '/'});
    this._bearer_token = '';
  };

  this.deleteRefreshToken = function () {
    var cookieDomain = this._getCookieDomain();
    if (cookieDomain) {
      Cookies.remove('X-Refresh-Token', {path: '/', domain: cookieDomain});
    }
    Cookies.remove('X-Refresh-Token', {path: '/'});
    this._refresh_token = '';
  };


  /*
        Create a new session.

        Arguments:
            data [object] (required):
                An object with username and password properties to authenticate with
                the server.
            callback [function] (required):
                Callback function to be invoked after attempting to create a session.
    */
  this.createSession = function (data, callback) {
    var request = new APIRequest(
      '/sessions',
      'POST',
      data,
      $.proxy(this._onCreateSession, this, callback)
    );
    request.send();
  };

  this._onCreateSession = function (callback, data, response) {
    if (response == 200 && typeof data.errors === 'undefined') {
      this.setAuthToken(data.auth_token);
      this.setBearerToken(data.bearer_token);
      this.setRefreshToken(data.refresh_token);
      callback(true, data);
    } else {
      this.deleteAuthToken();
      this.deleteBearerToken();
      this.deleteRefreshToken();
      callback(false, data);
    }
  };

  /*
        Deletes the current session.

        Arguments:
            callback [function] (required):
                Callback function to be invoked after deleting the session.
    */
  this.deleteSession = function (callback) {
    if (this._timer != null) {
      this._clearTimer();
    }

    // Remove all session cookies
    this._clearSessionCookies();

    // Send Session Delete action to backend
    var request = new APIRequest(
      '/sessions',
      'DELETE',
      null,
      $.proxy(this._onDeleteCallback, this, callback)
    );
    request.send();
  };

  /*
        Adds a uri to whitelist (requests made to whitelist uris do not renew the session).

        Arguments:
            uri [string] (required):
                The uri to whitelist.
    */
  this.addWhitelistUri = function (uri) {
    if (typeof uri === 'string' && !this._whitelistUris[uri]) {
      this._whitelistUris[uri] = uri;
    }
  };

  /*
        Removes a uri from the whitelist.

        Arguments:
            uri [string] (required):
                The uri to remove from the whitelist.
    */
  this.removeWhitelistUri = function (uri) {
    this._whitelistUris[uri] = null;
  };

  this._queuePing = function (time) {
    if (this._pendingPing !== false && this._auth_token != '') {
      clearTimeout(this._pendingPing);
      this._pendingPing = setTimeout($.proxy(this._refreshSession, this), time);
    }
  };

  this._onDeleteCallback = function (callback) {
    $(window).trigger('logout');

    this.deleteAuthToken();
    this.deleteBearerToken();
    this.deleteRefreshToken();

    if (typeof callback !== 'undefined') {
      callback();
    }
  };

  this.setSessionExpirationCallback = function (callback) {
    this._sessionExpirationCallback = callback;
  };

  this.setSessionTimeoutWarningCallback = function (callback) {
    this._sessionTimeoutWarningCallback = callback;
  };

  this._onSessionTimeout = function () {
    this.deleteSession(this._sessionExpirationCallback);
  };

  this._onAjaxRequest = function () {
    if (this._pendingPing !== false) {
      if (arguments && arguments[2]) {
        var uriEnd =
          arguments[2].url.indexOf('?') > -1
            ? arguments[2].url.indexOf('?')
            : arguments[2].url.length;
        var uri = arguments[2].url.substr(0, uriEnd);
        if (
          arguments[2].headers != null &&
          arguments[2].headers.Accept != null &&
          arguments[2].headers.Accept == mogo.registry.mogoUI.config.apiAcceptHeader
        ) {
          // API Calls
          if (this._pendingPing != null) clearTimeout(this._pendingPing);
          this._pendingPing = false;
          setTimeout(
            $.proxy(function () {
              this._pendingPing = null;
            }, this),
            this._MIN_TIME_BTWN_PINGS
          );
          var baseUri = uri.split('/trans'); // remove polling uri variables from whitelist check
          if (this._whitelistUris[baseUri[0]] === undefined) {
            this._resetTimer();
          }
        } else if (
          (arguments[2].headers == null ||
            arguments[2].headers.Accept == null ||
            arguments[2].headers.Accept != mogo.registry.mogoUI.config.apiAcceptHeader) &&
          arguments[2].crossDomain != true
        ) {
          // Non API Calls
          this._queuePing(1000);
        }
      }
    }
  };

  this._refreshSession = function () {
    if (document.readyState != 'complete') {
      this._queuePing(500);
      return;
    }
    var pingReq = new APIRequest('/accounts', 'GET', null, $.proxy(this._onSessionRefresh, this));
    pingReq.send();
  };

  this._onSessionRefresh = function () {
    this._pendingPing = false;
    setTimeout(
      $.proxy(function () {
        this._pendingPing = null;
      }, this),
      this._MIN_TIME_BTWN_PINGS
    );
  };

  this._resetTimer = function () {
    if (this._auth_token != '') {
      if (this._timer != null) {
        this._clearTimer();
      }
      this._timeoutWarning = setTimeout(
        $.proxy(this._sessionTimeoutWarningCallback, this),
        this._SESSION_TIMEOUT_WARNING
      );
      this._timer = setTimeout($.proxy(this._onSessionTimeout, this), this._SESSION_TIMEOUT);
    }
  };

  this._clearTimer = function () {
    clearTimeout(this._timer);
    this._timer = null;

    clearTimeout(this._timeoutWarning);
    this._timeoutWarning = null;
  };

  this.setSessionCookie = function (name, value) {
    Cookies.set(this._cookieSalt + name, value);
  };

  this.getSessionCookie = function (name) {
    return Cookies.get(this._cookieSalt + name);
  };

  this._clearSessionCookies = function () {
    for (var variable in Cookies.getJSON()) {
      if (Cookies.getJSON().hasOwnProperty(variable)) {
        if (variable.indexOf(this._cookieSalt) === 0) {
          Cookies.remove(variable);
        }
      }
    }
  };

  // Singleton
  window.mogo = window.mogo || {};
  window.mogo.registry = window.mogo.registry || {};
  if (window.mogo.registry.Session == null) {
    window.mogo.registry.Session = this;
    this.constructor();
  } else {
    window.mogo.registry.Session._queuePing(5000);
  }
  return window.mogo.registry.Session;
}
